import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ExternalLink, FileText } from "lucide-react";
import { mediaUrl, thumbUrl } from "@/lib/api";

export default function MediaGallery({ media, title }) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const current = media[active];

  const step = useCallback(
    (dir) => setActive((a) => (a + dir + media.length) % media.length),
    [media.length]
  );

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, step]);

  if (!current) return null;

  const renderMain = (m, inLightbox = false) => {
    const url = mediaUrl(m.storage_path);
    const displayUrl = thumbUrl(m);
    if (m.media_type === "video") {
      return (
        <video
          data-testid="media-video-player"
          src={url}
          controls
          playsInline
          preload="metadata"
          className="w-full max-h-[70vh] rounded-xl bg-charcoal-900"
        />
      );
    }
    if (m.media_type === "pdf") {
      return (
        <div className="w-full rounded-xl border border-charcoal-900/10 bg-surface-white p-10 flex flex-col items-center gap-4">
          <FileText className="w-10 h-10 text-copper-600" />
          <p className="text-sm text-charcoal-600">{m.original_filename || "PDF document"}</p>
          <a
            data-testid="media-pdf-open"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-6 py-3 hover:bg-copper-600 transition-colors"
          >
            Open PDF <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      );
    }
    return (
      <button
        data-testid="media-image-main"
        onClick={() => setLightbox(true)}
        className="block w-full cursor-zoom-in"
        aria-label={`Open ${title} image in lightbox`}
      >
        <img
          src={displayUrl}
          alt={m.alt_text || title}
          className={`w-full rounded-xl object-contain bg-surface-muted ${inLightbox ? "" : "max-h-[70vh]"}`}
        />
      </button>
    );
  };

  return (
    <div data-testid="media-gallery">
      <div className="rounded-2xl border border-charcoal-900/10 bg-surface-white p-3 sm:p-4">
        {renderMain(current)}
      </div>

      {media.length > 1 && (
        <div className="mt-4 flex gap-3 overflow-x-auto pb-2" data-testid="media-thumbs">
          {media.map((m, i) => (
            <button
              key={m.id}
              data-testid={`media-thumb-${i}`}
              onClick={() => setActive(i)}
              aria-label={`View media ${i + 1}`}
              aria-pressed={i === active}
              className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                i === active ? "border-copper-600" : "border-charcoal-900/10 hover:border-charcoal-900/30"
              }`}
            >
              {m.media_type === "image" ? (
                <img src={thumbUrl(m)} alt={m.alt_text || `${title} ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
              ) : (
                <span className="w-full h-full flex items-center justify-center bg-surface-muted text-copper-600">
                  {m.media_type === "video" ? <ChevronRight className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {lightbox && current.media_type === "image" && (
          <motion.div
            data-testid="media-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-charcoal-900/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setLightbox(false)}
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(false)}
              aria-label="Close lightbox"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-parchment/10 border border-parchment/20 flex items-center justify-center text-parchment hover:bg-parchment/20 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            {media.length > 1 && (
              <>
                <button
                  data-testid="lightbox-prev"
                  onClick={(e) => { e.stopPropagation(); step(-1); }}
                  aria-label="Previous image"
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-parchment/10 border border-parchment/20 flex items-center justify-center text-parchment hover:bg-parchment/20 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  data-testid="lightbox-next"
                  onClick={(e) => { e.stopPropagation(); step(1); }}
                  aria-label="Next image"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-parchment/10 border border-parchment/20 flex items-center justify-center text-parchment hover:bg-parchment/20 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
            <motion.img
              key={current.id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              src={mediaUrl(current.storage_path)}
              alt={current.alt_text || title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
